import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CustomerAPI, extractError } from "@/lib/api";
import { CUSTOMER_TYPES, COUNTRIES, IN_STATES } from "@/lib/india";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function CustomerEditPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { branches } = useAuth();
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        CustomerAPI.get(id).then((c) => {
            setForm({
                ...c,
                address: c.address || { line1: "", line2: "", city: "", state_code: "KA", postal_code: "", country_code: "IN" },
                gstin: c.gstin || "",
                email: c.email || "",
                alt_phone: c.alt_phone || "",
                last_name: c.last_name || "",
                notes: c.notes || "",
            });
        }).catch((e) => { toast.error(extractError(e)); nav("/customers"); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
    function setAddr(k, v) { setForm((f) => ({ ...f, address: { ...f.address, [k]: v } })); }

    async function save() {
        setBusy(true);
        try {
            const body = {
                first_name: form.first_name,
                last_name: form.last_name || null,
                phone: form.phone,
                alt_phone: form.alt_phone || null,
                email: form.email || null,
                gstin: form.gstin || null,
                customer_type: form.customer_type,
                primary_branch_id: form.primary_branch_id,
                tags: form.tags,
                notes: form.notes || null,
                address: form.address && form.address.line1 ? form.address : null,
                version: form.version,
            };
            const upd = await CustomerAPI.update(id, body);
            toast.success("Customer updated");
            nav(`/customers/${upd.id}`);
        } catch (e) {
            const code = e?.response?.data?.error?.code;
            if (code === "OPTIMISTIC_LOCK") {
                toast.error("This record was modified elsewhere. Reloading…");
                const fresh = await CustomerAPI.get(id);
                setForm({ ...form, ...fresh, address: fresh.address || form.address });
            } else {
                toast.error(extractError(e));
            }
        } finally {
            setBusy(false);
        }
    }

    if (!form) return <div className="text-sm text-zinc-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…</div>;

    return (
        <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="customer-edit-page">
            <header className="flex items-center justify-between">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{form.customer_code}</div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">Edit {form.display_name}</h1>
                </div>
                <Link to={`/customers/${id}`} className="text-sm text-zinc-500 hover:underline">← Back</Link>
            </header>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Contact</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="First name"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="cust-firstname" /></Field>
                    <Field label="Last name"><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="cust-lastname" /></Field>
                    <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="font-mono" data-testid="cust-phone" /></Field>
                    <Field label="Alt phone"><Input value={form.alt_phone} onChange={(e) => set("alt_phone", e.target.value)} className="font-mono" /></Field>
                    <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} type="email" /></Field>
                    <Field label="Type">
                        <select value={form.customer_type} onChange={(e) => set("customer_type", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                            {CUSTOMER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    {form.customer_type === "business" && (
                        <Field label="GSTIN"><Input value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} className="font-mono uppercase" data-testid="cust-gstin" /></Field>
                    )}
                    <Field label="Primary branch">
                        <select value={form.primary_branch_id || ""} onChange={(e) => set("primary_branch_id", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                            {branches.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
                        </select>
                    </Field>
                </div>
            </Card>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Address</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Line 1"><Input value={form.address.line1} onChange={(e) => setAddr("line1", e.target.value)} /></Field>
                    <Field label="Line 2"><Input value={form.address.line2 || ""} onChange={(e) => setAddr("line2", e.target.value)} /></Field>
                    <Field label="City"><Input value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} /></Field>
                    <Field label="State">
                        <select value={form.address.state_code} onChange={(e) => setAddr("state_code", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-mono">
                            {IN_STATES.map(([code, name]) => <option key={code} value={code}>{code} — {name}</option>)}
                        </select>
                    </Field>
                    <Field label="Postal code"><Input value={form.address.postal_code} onChange={(e) => setAddr("postal_code", e.target.value)} className="font-mono" /></Field>
                    <Field label="Country">
                        <select value={form.address.country_code} onChange={(e) => setAddr("country_code", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-mono">
                            {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{c} — {n}</option>)}
                        </select>
                    </Field>
                </div>
            </Card>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Notes</h2>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} />
            </Card>

            <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => nav(`/customers/${id}`)}>Cancel</Button>
                <Button onClick={save} disabled={busy} className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="cust-save">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save changes
                </Button>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{label}</Label>
            {children}
        </div>
    );
}
